import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { finalize, tap } from 'rxjs';
import { ApiError, HttpService } from '../../../services/http.service';
import { requiredTrimmed } from '../../../utils/utils';
import { InputComponent } from '../../from-input/input.component';
import { MultiSelectComponent } from '../../multi-select/multi-select.component';
import { CustomToastComponent } from '../../toast/custom-toast.component';
import { AddSchoolComponent } from '../add-school/add-school.component';

export function dobNotInFuture(
  control: AbstractControl,
): ValidationErrors | null {
  if (!control.value) return null;

  const selectedDate = new Date(control.value);
  const today = new Date();

  selectedDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return selectedDate > today ? { futureDob: true } : null;
}
interface StudentDialogData {
  initialData?: StudentData;
  EditMode: boolean;
  schoolID: string;
  eventId: string;
  addStudent: boolean;
  endpoint?: string;
}

interface StudentData {
  comp: any;
  past: any;
  yearLevelId: any;
  ethnicityId: string;
  pastAnswers: any;
  completedAnswers: any;
  id: any;
  name: any;
  dateOfBirth: string;
  schoolName: string;
  studentName: string;
  studentId: string;
  firstName: string;
  lastName: string;
  yearLevel: number;
  schoolEmail: string;
  mobileNumber: string;
  gender: string;
  postcode: string;
  teacherName: string;
  ethnicity: string;
  createdBy: string;
  email: string;
  school: string;
  schoolId: string;
  fullName: string;
  assignedTeacher: boolean;
  parentName: string;
  parentEmail: string;
  personalEmail: string;
  parentGuardianFullName: string;
  parentGuardianEmail: string;
  dob: string;
}

interface ApiDataResponse {
  status: boolean;
  message: string;
  data: StudentData | StudentData[];
}

@Component({
  selector: 'app-add-student',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    MatDialogModule,
    MultiSelectComponent,
  ],
  templateUrl: './add-student.component.html',
  styleUrls: ['./add-student.component.scss'],
})
export class AddStudentComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  isProcessing: boolean = false;
  isEdit: boolean = false;
  addStudent: boolean = true;
  isSchoolSelected: boolean = false;
  schoolOptions: any[] = [];
  fullSchoolOptions: any[] = [];
  studentOptions: any[] = [];
  fullStudentOptions: any[] = [];
  teachersOption: any[] = [];
  yearLevelOption: any[] = [];
  ethnicityOption: any[] = [];
  genderOption: any[] = [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
    { label: 'Non-identifying', value: 'non-identifying' },
    { label: 'Prefer not to say', value: 'prefernottosay' },
  ];

  selectedStudentIDs: any[] = [];

  dataOption: any[] = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ];
  showConstructionRelatedCourseAnswers: boolean = false;
  showConstructionCourseAnswers: boolean = false;
  showPastAnswersError: boolean = false;
  showCompAnswersError: boolean = false;
  addNewStudent: boolean = false;

  constructionPastAnswers: any[] = [];
  constructionCompAnswers: any[] = [];
  loading = false;
  role = localStorage.getItem('userRole');
  selectedSchoolID: any = '';
  selectedStudentID: any = '';
  selectedEventID: any = '';
  customErrorMessages = {
    firstName: { required: 'Please enter a first name.' },
    lastName: { required: 'Please enter a last name.' },
    personalEmail: {
      required: 'Please enter a personal email address.',
      email: 'Please enter a valid email address.',
      pattern: 'Please enter a valid email address.',
    },
    schoolEmail: {
      required: 'Please enter a school email address.',
      email: 'Please enter a valid school email address.',
      pattern: 'Please enter a valid school email address.',
    },
    parentEmail: {
      required: 'Please enter a parent/guardian email address.',
      email: 'Please enter a valid parent/guardian email address.',
      pattern: 'Please enter a valid parent/guardian email address.',
    },
    parentName: {
      required: 'Please enter a parent or guardian full name.',
    },
    dob: {
      required: 'Please enter a date of birth.',
      futureDob: 'Date of birth cannot be a future date.',
    },
    schoolName: { required: 'Please select a school.' },
    gender: { required: 'Please select a gender.' },
    ethnicity: { required: 'Please select an ethnicity.' },
    studentName: { required: 'Please select a student.' },
    yearLevel: {
      required: 'Please select a year level.',
      pattern: 'Please enter a year level in numeric.',
      min: 'Please enter a year level must be at least 1',
      max: 'Please enter a year level must be 12 or less',
    },
    constructionCourse: {
      required: 'Please select Yes or No for question 1.',
    },
    constructionRelatedCourse: {
      required: 'Please select Yes or No for question 2.',
    },
  } as const;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<AddStudentComponent>,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private router: Router,

    @Inject(MAT_DIALOG_DATA) public data: StudentDialogData,
  ) {
    this.isEdit = data?.EditMode || false;
    this.selectedSchoolID = data?.schoolID || '';
    this.selectedEventID = data?.eventId || '';
    this.addStudent = data.addStudent;
    this.addNewStudent = !data.addStudent;
    this.initForm();
  }

  ngOnInit(): void {
    this.fetchYearLevelDropDown().subscribe();
    this.fetchEthnicityDropDown().subscribe();
    this.fetchPastAnswers().subscribe();
    this.fetchCompAnswers().subscribe();
    this.fetchSchoolsDropDown().subscribe({
      next: () => {
        if (this.selectedSchoolID) {
          this.patchSchoolFromSelectedID();
        }
        if (this.isEdit && this.data.initialData) {
          setTimeout(() => {
            this.patchFormForEdit(this.data.initialData!);
          }, 100);
        }
      },
    });

    if (this.addStudent) {
      this.fetchStudentsDropDown().subscribe({});
    }

    this.form.get('studentName')?.valueChanges.subscribe((value) => {
      if (this.role === 'teacher') {
        this.selectedStudentIDs = Array.isArray(value) ? value : [];
        this.selectedStudentID = this.selectedStudentIDs[0];
      } else {
        this.selectedStudentID = value;
      }
    });
    this.form.get('constructionCourse')?.valueChanges.subscribe((val) => {
      this.showConstructionCourseAnswers = val === 'yes';
      this.showPastAnswersError = false;
      if (val === 'no') {
        this.form.get('pastAnswers')?.setValue([]);
      }
    });

    this.form
      .get('constructionRelatedCourse')
      ?.valueChanges.subscribe((val) => {
        this.showConstructionRelatedCourseAnswers = val === 'yes';
        this.showCompAnswersError = false;
        if (val === 'no') {
          this.form.get('completedAnswers')?.setValue([]);
        }
      });

    this.updateValidators();
  }

  ngOnDestroy(): void {}

  onStudentSelectionChange(selectedValues: any[]): void {
    this.selectedStudentIDs = selectedValues;
    this.selectedStudentID = selectedValues[0];
    this.form
      .get('studentName')
      ?.setValue(selectedValues, { emitEvent: false });
    if (this.role === 'teacher' && selectedValues.length === 0) {
      this.form.get('studentName')?.setErrors({ required: true });
    }
  }

  getStudentLabel(id: string): string {
    return this.studentOptions.find((o) => o.value === id)?.label ?? id;
  }

  removeStudentChip(id: string): void {
    this.selectedStudentIDs = this.selectedStudentIDs.filter((s) => s !== id);
    this.selectedStudentID = this.selectedStudentIDs[0] ?? null;
    this.form.get('studentName')?.setValue(this.selectedStudentIDs, { emitEvent: false });
    if (this.role === 'teacher' && this.selectedStudentIDs.length === 0) {
      this.form.get('studentName')?.setErrors({ required: true });
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      schoolName: [''],
      firstName: [''],
      lastName: [''],
      dob: [''],
      schoolEmail: [''],
      parentName: [''],
      parentEmail: [''],
      studentName: [[]],
      personalEmail: [''],
      gender: [''],
      ethnicity: [''],
      yearLevel: [null],
      constructionCourse: [''],
      constructionRelatedCourse: [''],
      pastAnswers: [[]],
      completedAnswers: [[]],
    });

    this.updateValidators();
  }

  private updateValidators(): void {
    if (this.addStudent) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.get(key)?.clearValidators();
      });
      this.form.get('studentName')?.setValidators([
        (control: AbstractControl): ValidationErrors | null => {
          const value = control.value;
          if (!value || !Array.isArray(value) || value.length === 0) {
            return { required: true };
          }
          return null;
        },
      ]);
    } else {
      this.form.get('schoolName')?.setValidators([requiredTrimmed]);
      this.form.get('firstName')?.setValidators([requiredTrimmed]);
      this.form.get('lastName')?.setValidators([requiredTrimmed]);
      this.form
        .get('dob')
        ?.setValidators([Validators.required, dobNotInFuture]);
      this.form
        .get('schoolEmail')
        ?.setValidators([
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
        ]);
      this.form.get('parentName')?.setValidators([requiredTrimmed]);
      this.form
        .get('parentEmail')
        ?.setValidators([
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
        ]);
      this.form
        .get('personalEmail')
        ?.setValidators([
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
        ]);
      this.form.get('studentName')?.setValidators([requiredTrimmed]);
      this.form.get('gender')?.setValidators([requiredTrimmed]);
      this.form.get('ethnicity')?.setValidators([requiredTrimmed]);
      this.form.get('constructionCourse')?.setValidators([Validators.required]);
      this.form
        .get('constructionRelatedCourse')
        ?.setValidators([Validators.required]);
      this.form.get('studentName')?.clearValidators();
    }

    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.updateValueAndValidity();
    });
  }

  private patchSchoolFromSelectedID(): void {
    if (this.selectedSchoolID && this.schoolOptions.length > 0) {
      const selectedSchool = this.schoolOptions.find(
        (opt) => opt.value.toString() === this.selectedSchoolID.toString(),
      );

      if (selectedSchool) {
        const schoolControl = this.form.get('schoolName');
        if (schoolControl) {
          schoolControl.setValue(selectedSchool.value, { emitEvent: true });
          schoolControl.markAsTouched();
          if (this.role === 'teacher') {
            schoolControl.disable();
          }
          return;
          this.cdr.detectChanges();
        }
      } else {
        const fullSchool = this.fullSchoolOptions.find(
          (opt) => opt.value.toString() === this.selectedSchoolID.toString(),
        );

        if (fullSchool) {
          this.schoolOptions = [fullSchool, ...this.schoolOptions];
          this.form
            .get('schoolName')
            ?.setValue(this.selectedSchoolID, { emitEvent: true });

          if (this.role === 'teacher') {
            this.form.get('schoolName')?.disable();
          }
          this.cdr.detectChanges();
        } else {
          this.schoolOptions.unshift({
            label: 'Selected School',
            value: this.selectedSchoolID,
          });
          this.form
            .get('schoolName')
            ?.setValue(this.selectedSchoolID, { emitEvent: true });
          if (this.role === 'teacher') {
            this.form.get('schoolName')?.disable();
          }
          this.cdr.detectChanges();
        }
      }
    }
  }

  private patchFormForEdit(studentData: StudentData): void {
    const selectedGenderOption = this.genderOption?.find(
      (option) =>
        option?.value?.toLowerCase() ===
        (studentData?.gender || '').toLowerCase(),
    );

    const selectedEthnicity = this.ethnicityOption?.find(
      (option) =>
        option?.label?.toLowerCase() ===
          (studentData?.ethnicity || '').toLowerCase() ||
        option?.value === studentData?.ethnicityId,
    );

    const selectedYearLevel = this.yearLevelOption?.find(
      (option) =>
        option?.label === studentData?.yearLevel?.toString() ||
        option?.value === studentData?.yearLevelId,
    );
    const pastCourseIds =
      studentData?.past
        ?.filter((item: any) => item.value === true)
        .map((item: any) => item.courseId) || [];
    const compCourseIds =
      studentData?.comp
        ?.filter((item: any) => item.value === true)
        .map((item: any) => item.courseId) || [];

    const formValues = {
      firstName: this.coalesce(studentData?.firstName, ''),
      lastName: this.coalesce(studentData?.lastName, ''),
      yearLevel: this.coalesce(
        selectedYearLevel?.value,
        studentData?.yearLevelId,
        studentData?.yearLevel,
        null,
      ),
      schoolEmail: this.coalesce(
        studentData?.schoolEmail,
        studentData?.email,
        '',
      ),
      dob: this.coalesce(studentData?.dob, studentData?.dateOfBirth, null),
      personalEmail: this.coalesce(
        studentData?.personalEmail,
        studentData?.['personalEmail'],
        null,
      ),
      ethnicity: this.coalesce(
        selectedEthnicity?.value,
        studentData?.ethnicityId,
        studentData?.ethnicity,
        null,
      ),
      gender: this.coalesce(
        selectedGenderOption?.value,
        studentData?.gender,
        null,
      ),
      parentName: this.coalesce(
        studentData?.parentName,
        studentData?.parentGuardianFullName,
        '',
      ),
      parentEmail: this.coalesce(
        studentData?.parentEmail,
        studentData?.parentGuardianEmail,
        '',
      ),
      constructionCourse: pastCourseIds.length > 0 ? 'yes' : 'no',
      constructionRelatedCourse: compCourseIds.length > 0 ? 'yes' : 'no',
      pastAnswers: pastCourseIds,
      completedAnswers: compCourseIds,
    };

    const cleanFormValues = Object.entries(formValues).reduce(
      (acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          acc[key] = value;
        }
        return acc;
      },
      {} as any,
    );

    this.form.patchValue(cleanFormValues);
    this.showConstructionCourseAnswers = pastCourseIds.length > 0;
    this.showConstructionRelatedCourseAnswers = compCourseIds.length > 0;

    const selectedSchoolOption = this.schoolOptions?.find(
      (option) =>
        option?.label?.toLowerCase() ===
          (
            studentData?.schoolName ||
            studentData?.school ||
            ''
          ).toLowerCase() || option?.value === studentData?.schoolId,
    );

    if (selectedSchoolOption?.value) {
      this.form.get('schoolName')?.setValue(selectedSchoolOption.value);
    } else if (studentData?.schoolId) {
      this.schoolOptions.unshift({
        label:
          studentData?.schoolName || studentData?.school || 'Selected School',
        value: studentData.schoolId,
      });
      this.form.get('schoolName')?.setValue(studentData.schoolId);
    }

    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.markAsTouched();
    });

    this.cdr.detectChanges();
  }

  private coalesce(...values: any[]): any {
    for (const value of values) {
      if (value !== null && value !== undefined) {
        return value;
      }
    }
    return null;
  }

  onSubmit(): void {
    if (this.isProcessing) return;
    this.markFormGroupTouched(this.form);

    // Explicit school validation for add-new-student mode
    if (this.addNewStudent) {
      const schoolValue = this.form.get('schoolName')?.value;
      if (!schoolValue || (typeof schoolValue === 'string' && !schoolValue.trim())) {
        this.scrollToFirstInvalidControl();
        return;
      }
    }

    const constructionCourseValue = this.form.get('constructionCourse')?.value;
    const constructionRelatedValue = this.form.get(
      'constructionRelatedCourse',
    )?.value;
    const pastAnswers = this.form.get('pastAnswers')?.value || [];
    const completedAnswers = this.form.get('completedAnswers')?.value || [];

    if (constructionCourseValue === 'yes' && pastAnswers.length === 0) {
      this.showPastAnswersError = true;
    }
    if (constructionRelatedValue === 'yes' && completedAnswers.length === 0) {
      this.showCompAnswersError = true;
    }

    if (this.showPastAnswersError || this.showCompAnswersError) {
      setTimeout(() => {
        // Scroll to first checkbox error if present, else fall back to first ng-invalid
        const checkboxError = document.querySelector(
          '.error-message-container',
        );
        const firstInvalid = document.querySelector('.ng-invalid:not(form)');
        const target = checkboxError || firstInvalid;
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          (target as HTMLElement).focus();
        }
      }, 100);
      return;
    }

    if (this.form.valid) {
      this.isProcessing = true;
      const payload = this.buildPayload();
      const studentIDs = this.form.get('studentName')?.value || [];

      let request;
      if (this.selectedEventID) {
        const effectiveEndpoint =
          this.data.endpoint ?? (this.role === 'teacher' ? 'TeacherEvent' : 'events');
        if (studentIDs.length > 0 && this.role === 'teacher') {
          request = this.httpService.post<ApiDataResponse>(
            `${effectiveEndpoint}/${this.selectedEventID}/assign-existing`,
            { studentIds: studentIDs },
          );
        } else if (studentIDs.length > 0 && this.role === 'superadmin') {
          request = this.httpService.post<ApiDataResponse>(
            `events/${this.selectedEventID}/assign-existing`,
            { studentIds: studentIDs },
          );
        } else if (
          studentIDs.length > 0 &&
          this.role === 'teacher' &&
          this.data.initialData?.createdBy === 'Super Admin'
        ) {
          request = this.httpService.post<ApiDataResponse>(
            `events/${this.selectedEventID}/assign-existing`,
            { studentIds: studentIDs },
          );
        } else if (this.addNewStudent) {
          if (this.role === 'teacher') {
            request = this.httpService.post<ApiDataResponse>(
              `${effectiveEndpoint}/${this.selectedEventID}/assign-new`,
              payload,
            );
          } else {
          request = this.httpService.post<ApiDataResponse>(
            `events/${this.selectedEventID}/assign-new`,
            payload,
          );
          }
        } else {
          const studentId = studentIDs[0];
          request =
            this.role === 'teacher'
              ? this.httpService.post<ApiDataResponse>(
                  `${effectiveEndpoint}/${this.selectedEventID}/assign-existing/${studentId}`,
                  {},
                )
              : this.httpService.post<ApiDataResponse>(
                  `events/${this.selectedEventID}/assign-existing/${studentId}`,
                  {},
                );
        }
      } else {
        request = this.isEdit
          ? this.httpService.put<ApiDataResponse>(
              `students/${studentIDs[0] || this.data.initialData?.studentId || this.data.initialData?.id}`,
              payload,
              true,
            )
          : this.httpService.post<ApiDataResponse>('students', payload, true);
      }

      request
        .pipe(
          finalize(() => {
            this.isProcessing = false;
            this.form.enable();
          }),
        )
        .subscribe({
          next: (response: any) => {
            if (!this.isEdit && !this.selectedEventID) {
              let studentId = response?.data?.studentId ?? studentIDs[0];
              this.router.navigate(['/students/view', studentId]);
            }
            this.handleSubmitResponse(response);
          },
          error: (err: ApiError) => {
            if (err) {
              this.showErrorToast(err?.message);
            } else {
              this.showErrorToast(
                'Something went wrong. Please try again later.',
              );
            }
          },
        });
    } else {
      this.scrollToFirstInvalidControl();
    }
  }

  private buildPayload(): any {
    const formData = this.form.getRawValue();

    const pastCourses = this.constructionPastAnswers.map((course) => ({
      courseId: course.id,
      courseName: course.courseName,
      value:
        formData.constructionCourse === 'yes'
          ? formData.pastAnswers?.includes(course.id) || false
          : false,
    }));

    const compCourses = this.constructionCompAnswers.map((course) => ({
      courseId: course.id,
      courseName: course.courseName,
      value:
        formData.constructionRelatedCourse === 'yes'
          ? formData.completedAnswers?.includes(course.id) || false
          : false,
    }));
    const basePayload: any = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      schoolEmail: formData.schoolEmail,
      dateOfBirth: formData.dob,
      schoolId: formData.schoolName,
      yearLevelId: formData.yearLevel || null,
      gender: formData.gender,
      ethnicityId: formData.ethnicity,
      parentGuardianFullName: formData.parentName,
      parentGuardianEmail: formData.parentEmail,
      past: pastCourses,
      comp: compCourses,
    };

    if (formData.personalEmail) {
      basePayload.personalEmail = formData.personalEmail;
    }

    if (
      this.selectedEventID &&
      this.addStudent &&
      Array.isArray(formData.studentName) &&
      formData.studentName.length > 0
    ) {
      return {
        ...basePayload,
        studentIds: formData.studentName,
      };
    }

    return basePayload;
  }

  private handleSubmitResponse(response: ApiDataResponse): void {
    if (response.status) {
      const message = this.isEdit
        ? 'Student updated successfully'
        : 'Student created successfully';
      this.showSuccessToast(response.message || message);
      this.dialogRef.close({ success: true, data: response.data });
    } else {
      this.showErrorToast(
        response.message || 'Something went wrong. Please try again later.',
      );
    }
  }

  onCancel(): void {
    if (this.isProcessing) return;
    this.dialogRef.close(false);
  }
  onAddNewSchool(): void {
    if (this.isProcessing) return;
    const currentSelectedSchool = this.form.get('schoolName')?.value;

    const dialogRef = this.dialog.open(AddSchoolComponent, {
      width: '50.5rem',
      maxWidth: '90vw',
      minWidth: '300px',
      data: { initialData: undefined, EditMode: false },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.fetchSchoolsDropDown().subscribe(() => {
          setTimeout(() => this.autoSelectNewSchool(result.data), 150);
        });
      } else {
        if (currentSelectedSchool) {
          const schoolExists = this.schoolOptions.some(
            (opt) => opt.value === currentSelectedSchool,
          );
          if (schoolExists) {
            this.form
              .get('schoolName')
              ?.setValue(currentSelectedSchool, { emitEvent: true });
          } else {
            const fullSchool = this.fullSchoolOptions.find(
              (opt) => opt.value === currentSelectedSchool,
            );
            if (fullSchool) {
              this.schoolOptions = [fullSchool, ...this.schoolOptions];
              this.form
                .get('schoolName')
                ?.setValue(currentSelectedSchool, { emitEvent: true });
            }
          }
        }
        this.cdr.detectChanges();
      }
    });
  }

  onAddNewStudent(): void {
    this.addStudent = !this.addStudent;
    this.addNewStudent = !this.addStudent;
    this.selectedStudentID = null;
    this.form.reset();
    if (this.selectedSchoolID && !this.addStudent) {
      setTimeout(() => {
        this.patchSchoolFromSelectedID();
      }, 0);
    }

    this.updateValidators();
    this.cdr.detectChanges();
  }

  fetchStudentsDropDown() {
    this.loading = true;
    const type = 2;
    return this.httpService
      .get<ApiDataResponse>(
        `events/events/${this.selectedEventID}/students-dropdown`,
      )
      .pipe(
        tap({
          next: (response) => {
            if (Array.isArray(response.data)) {
              this.studentOptions = response.data.map((item) => ({
                label: item.fullName,
                value: item.studentId,
              }));

              if (this.isEdit && this.data.initialData) {
                const exists = this.studentOptions.find(
                  (option: { value: any }) =>
                    option.value === this.data.initialData?.schoolId,
                );

                if (!exists && this.data.initialData.schoolId) {
                  this.studentOptions.unshift({
                    label: this.data.initialData.schoolName,
                    value: this.data.initialData.schoolId,
                  });
                }
              }
            } else {
              this.showErrorToast(
                'Something went wrong. Please try again later.',
              );
            }
          },
          error: (err) => {
            this.studentOptions = [];
            this.showErrorToast('Failed to load school dropdown');
          },
        }),
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        }),
      );
  }

  onSelectChange(event: any, question: 1 | 2): void {
    const value = event.target.value;
    const checked = event.target.checked;
    const controlName = question === 1 ? 'pastAnswers' : 'completedAnswers';
    const current: string[] = this.form.get(controlName)?.value ?? [];

    const updated = checked
      ? [...current, value]
      : current.filter((v) => v !== value);

    this.form.get(controlName)?.setValue(updated);
    if (question === 1) this.showPastAnswersError = false;
    if (question === 2) this.showCompAnswersError = false;
  }

  private autoSelectNewSchool(data: any): void {
    const schoolId = data.id || data.schoolId;
    if (schoolId && this.form.get('schoolName')) {
      const schoolExists = this.schoolOptions.some(
        (opt) => opt.value === schoolId,
      );
      if (!schoolExists) {
        const newSchoolOption = {
          label: data.name || data.schoolName,
          value: schoolId,
        };
        this.schoolOptions = [newSchoolOption, ...this.schoolOptions];
      }

      this.form.get('schoolName')?.setValue(schoolId, { emitEvent: true });
      this.showSuccessToast('School added and selected successfully');
      this.cdr.detectChanges();
    }
  }

  fetchSchoolsDropDown() {
    this.loading = true;
    const type = 2;
    let url = `Schools/dropdown`;

    if (this.selectedEventID) {
      url = `events/specfic/dropdown/${this.selectedEventID}`;
    } else {
      url = `${url}?status=${type}`;
    }

    const currentSelectedValue = this.form.get('schoolName')?.value;

    return this.httpService.get<ApiDataResponse>(url).pipe(
      tap({
        next: (response) => {
          if (Array.isArray(response.data)) {
            const activeSchools = response.data.filter(
              (item: any) => item.status === 'Active',
            );

            const newFullSchoolOptions = activeSchools.map((item) => ({
              label: item.name || item.schoolName,
              value: item.id || item.schoolId,
            }));

            let newSchoolOptions: any[] = [];

            // if (this.selectedSchoolID) {
            //   const matchedSchool = newFullSchoolOptions.find(
            //     (option) =>
            //       option.value.toString() === this.selectedSchoolID.toString(),
            //   );

            //   if (matchedSchool) {
            //     newSchoolOptions = [matchedSchool];
            //   } else {
            //     newSchoolOptions = [...newFullSchoolOptions];
            //     console.warn(
            //       'School not found with ID:',
            //       this.selectedSchoolID,
            //     );
            //   }
            // } else {
            //   newSchoolOptions = [...newFullSchoolOptions];
            // }

            // if (currentSelectedValue) {
            //   const selectedExists = newSchoolOptions.some(
            //     (opt) => opt.value === currentSelectedValue,
            //   );
            //   if (!selectedExists) {
            //     const missingSchool = newFullSchoolOptions.find(
            //       (opt) => opt.value === currentSelectedValue,
            //     );
            //     if (missingSchool) {
            //       newSchoolOptions.unshift(missingSchool);
            //     }
            //   }
            // }

            // if (this.isEdit && this.data.initialData) {
            //   const exists = newSchoolOptions.some(
            //     (option: { value: any }) =>
            //       option.value === this.data.initialData?.schoolId,
            //   );

            //   if (!exists && this.data.initialData.schoolId) {
            //     newSchoolOptions.unshift({
            //       label: this.data.initialData.schoolName,
            //       value: this.data.initialData.schoolId,
            //     });
            //   }
            // }

            this.schoolOptions = newFullSchoolOptions;
            this.fullSchoolOptions = newFullSchoolOptions;

            if (currentSelectedValue) {
              setTimeout(() => {
                const schoolControl = this.form.get('schoolName');
                if (schoolControl && !schoolControl.value) {
                  schoolControl.setValue(currentSelectedValue, {
                    emitEvent: true,
                  });
                }
              });
            }
          } else {
            this.showErrorToast(
              'Something went wrong. Please try again later.',
            );
          }
        },
        error: (err) => {
          this.schoolOptions = [];
          this.showErrorToast('Failed to load school dropdown');
        },
      }),
      finalize(() => {
        this.loading = false;
        this.cdr.detectChanges();
      }),
    );
  }

  fetchYearLevelDropDown() {
    const type = 2;
    const status = true;
    return this.httpService
      .get<any>(`settings/yearlevel/dropdown?status=${status}`)
      .pipe(
        tap({
          next: (response) => {
            if (Array.isArray(response.data)) {
              const activeEventTypes = response.data.filter(
                (item: any) => item.status === 'Active',
              );
              this.yearLevelOption = response.data.map(
                (item: { name: any; id: any }) => ({
                  label: item.name,
                  value: item.id,
                }),
              );
            } else {
              this.showErrorToast('Invalid event type data format');
            }
          },
          error: (err) => {
            this.showErrorToast(err?.message);
          },
        }),
      );
  }

  fetchEthnicityDropDown() {
    const type = 2;
    const status = true;
    return this.httpService
      .get<any>(`settings/ethnicity/dropdown?status=${status}`)
      .pipe(
        tap({
          next: (response) => {
            if (Array.isArray(response.data)) {
              const activeEventTypes = response.data.filter(
                (item: any) => item.status === 'Active',
              );
              this.ethnicityOption = response.data.map(
                (item: { name: any; id: any }) => ({
                  label: item.name,
                  value: item.id,
                }),
              );
            } else {
              this.showErrorToast('Invalid event type data format');
            }
          },
          error: (err) => {
            this.showErrorToast(err?.message);
          },
        }),
      );
  }

  fetchCompAnswers() {
    const APIURL =
      this.role === 'teacher'
        ? 'teacherstudent/con-rel-course-comp'
        : 'students/con-rel-course-comp';
    return this.httpService.get<any>(APIURL).pipe(
      tap({
        next: (response) => {
          if (Array.isArray(response.data)) {
            this.constructionCompAnswers = response.data;
          } else {
            this.showErrorToast('Invalid event type data format');
          }
        },
        error: (err) => {
          this.showErrorToast(err?.message);
        },
      }),
    );
  }

  fetchPastAnswers() {
    const APIURL =
      this.role === 'teacher'
        ? 'teacherstudent/con-rel-course-past'
        : 'students/con-rel-course-past';
    return this.httpService.get<any>(APIURL).pipe(
      tap({
        next: (response) => {
          if (Array.isArray(response.data)) {
            this.constructionPastAnswers = response.data;
          } else {
            this.showErrorToast('Invalid event type data format');
          }
        },
        error: (err) => {
          this.showErrorToast(err?.message);
        },
      }),
    );
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      control?.updateValueAndValidity();
    });
  }

  private scrollToFirstInvalidControl(): void {
    setTimeout(() => {
      const firstInvalidControl = document.querySelector(
        '.ng-invalid:not(form)',
      );
      if (firstInvalidControl) {
        firstInvalidControl.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        (firstInvalidControl as HTMLElement).focus();
      }
    }, 100);
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: [
        'no-padding-snackbar',
        type === 'success' ? 'success-toast' : 'error-toast',
      ],
    });
  }

  private showSuccessToast(message: string): void {
    this.showToast(message, 'success');
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.showToast(message, 'error');
  }

  getFormErrors(): Record<string, any> {
    return Object.fromEntries(
      Object.entries(this.form.controls).filter(
        ([, control]) => !!control.errors,
      ),
    ) as Record<string, any>;
  }
}
